import { useIsPending } from './use-is-pending';
import { useSpinDelay, type UseSpinDelayOptions } from './use-spin-delay';

/**
 * Like {@link useIsPending}, but passes the pending flag through
 * {@link useSpinDelay} so a spinner appears only for slow submissions
 * and, once shown, stays visible long enough to avoid a flash. Use it to
 * drive loading state on forms and submit buttons.
 *
 * @param formAction Action URL to watch. Defaults to the current route's
 *   form action.
 * @param formMethod HTTP method to match. Defaults to `POST`.
 * @param delay Milliseconds to wait before showing pending. Defaults to
 *   400.
 * @param minDuration Minimum milliseconds to keep pending visible once
 *   shown. Defaults to 300.
 * @returns True while the delayed pending state is active.
 */
export function useDelayedIsPending({
  formAction,
  formMethod,
  delay = 400,
  minDuration = 300,
}: Parameters<typeof useIsPending>[0] & UseSpinDelayOptions = {}) {
  const isPending = useIsPending({ formAction, formMethod });
  const delayedIsPending = useSpinDelay(isPending, {
    delay,
    minDuration,
  });
  return delayedIsPending;
}
